import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { useEngineMaturityLevels } from '@/features/maturity-levels/hooks'
import type { MaturityLevel } from '@/features/maturity-levels/types'

interface ScoreSummaryProps {
  overallScore: number
  maturityLevel: number
  desiredMaturityLevel?: number | null
  assessmentScope?: string
  scopeLevel?: number | null
  domainScores: Array<{
    domainId: string
    domainName: string
    score: number
    percentage: number
    maturityLevel?: number
    maturityLevelName?: string
  }>
  industryBenchmarks?: Record<string, { target_level: number; description?: string }> | null
}

const FALLBACK_LEVEL: MaturityLevel = { level: 0, name: '—', description: '', color: '#6b7280', is_active: true, characteristics: [], capabilities: [], challenges: [] }

export function ScoreSummary({
  overallScore: _overallScore,
  maturityLevel,
  desiredMaturityLevel,
  assessmentScope = 'complete',
  scopeLevel,
  domainScores,
  industryBenchmarks,
}: ScoreSummaryProps) {
  const { data: maturityLevelsData } = useEngineMaturityLevels()
  const levels: MaturityLevel[] = maturityLevelsData?.data ?? maturityLevelsData ?? []

  const findLevel = (lvl: number) => levels.find(l => l.level === lvl) ?? FALLBACK_LEVEL
  const currentLevel = findLevel(maturityLevel)
  const nextLevel = findLevel(maturityLevel + 1)
  const levelColor = currentLevel.color || '#6b7280'

  /** Look up a benchmark for a given domain name */
  const getBenchmark = (domainName: string) => {
    if (!industryBenchmarks) return null
    const key = domainName.toLowerCase().replace(/\s+/g, '_')
    return industryBenchmarks[key] ?? industryBenchmarks[domainName] ?? null
  }

  const getGapBadge = (level: number, target: number) => {
    if (level >= target) return <Badge className="bg-green-100 text-green-800 text-[10px] px-1.5 py-0">Met</Badge>
    if (level >= target - 1) return <Badge className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0">Close</Badge>
    return <Badge className="bg-red-100 text-red-800 text-[10px] px-1.5 py-0">Below</Badge>
  }

  /** Derive the effective target level for the scope */
  const effectiveTarget = assessmentScope === 'level' && scopeLevel
    ? scopeLevel
    : desiredMaturityLevel ?? null

  /** Render scope-specific comparison info in the Overall Score card */
  const renderScopeInfo = () => {
    if (assessmentScope === 'level' && effectiveTarget) {
      const diff = maturityLevel - effectiveTarget
      return (
        <div className="mt-4 pt-3 border-t">
          <div className="text-xs text-gray-500">Target Level</div>
          <div className="text-lg font-semibold text-blue-600">Level {effectiveTarget}</div>
          <div className={`text-xs font-medium mt-1 ${diff >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {diff >= 0 ? '✓ Target met' : `${Math.abs(diff)} level${Math.abs(diff) > 1 ? 's' : ''} to go`}
          </div>
        </div>
      )
    }

    if (assessmentScope === 'industry' && industryBenchmarks) {
      const targets = Object.values(industryBenchmarks).map((b: any) => b.target_level)
      const avg = targets.length ? targets.reduce((a: number, b: number) => a + b, 0) / targets.length : null
      if (avg === null) return null
      const diff = maturityLevel - avg
      return (
        <div className="mt-3 pt-3 border-t">
          <div className="text-xs text-gray-500">Industry Standard</div>
          <div className="text-lg font-semibold text-amber-600">Level {avg.toFixed(1)}</div>
          <div className={`text-xs font-medium mt-1 ${diff >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {diff >= 0 ? '▲' : '▼'} {Math.abs(diff).toFixed(1)} levels {diff >= 0 ? 'above' : 'below'} target
          </div>
        </div>
      )
    }

    // 'complete' scope — just show score, no comparison
    return null
  }

  /** Should we show benchmark target markers on progress bars */
  const showBenchmarkMarkers = assessmentScope === 'industry' && !!industryBenchmarks
  const showTargetMarker = assessmentScope === 'level' && !!effectiveTarget

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Overall Score */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Score</CardTitle>
          <CardDescription>IT Maturity Assessment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="text-4xl font-bold" style={{ color: levelColor }}>
              Level {maturityLevel}
            </div>
            <div className="text-sm text-gray-500 mt-1">of 5 — {currentLevel.name}</div>
            <Progress value={maturityLevel * 20} className="mt-4" />
            {renderScopeInfo()}
          </div>
        </CardContent>
      </Card>

      {/* Maturity Level */}
      <Card>
        <CardHeader>
          <CardTitle>Maturity Level</CardTitle>
          <CardDescription>Current Assessment Level</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="text-4xl font-bold" style={{ color: levelColor }}>
              {maturityLevel}
            </div>
            <div className="text-lg font-semibold text-gray-900 mt-1">
              {currentLevel.name}
            </div>
            <div className="text-sm text-gray-600 mt-2">
              {currentLevel.description}
            </div>
            {maturityLevel < 5 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500">Next Level</div>
                <div className="text-sm font-medium text-gray-900">
                  {nextLevel.name}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Domain Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Domain Breakdown</CardTitle>
          <CardDescription>Performance by Domain</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {domainScores.map((domain) => {
              const bm = getBenchmark(domain.domainName)
              return (
                <div key={domain.domainId}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {domain.domainName}
                      </div>
                      <div className="relative mt-1">
                        <Progress value={(domain.maturityLevel ?? 1) * 20} className="h-2" />
                        {showTargetMarker && effectiveTarget && (
                          <div
                            className="absolute top-0 h-2 w-0.5 bg-blue-500 rounded"
                            style={{ left: `${effectiveTarget * 20}%` }}
                            title={`Target: Level ${effectiveTarget}`}
                          />
                        )}
                        {showBenchmarkMarkers && bm && (
                          <div
                            className="absolute top-0 h-2 w-0.5 bg-amber-500"
                            style={{ left: `${bm.target_level * 20}%` }}
                            title={`Industry Target: Level ${bm.target_level}`}
                          />
                        )}
                      </div>
                    </div>
                    <div className="ml-3 flex items-center gap-1.5 shrink-0">
                      <span className="text-sm font-semibold" style={{ color: findLevel(domain.maturityLevel ?? 1).color || '#6b7280' }}>
                        L{domain.maturityLevel ?? 1}
                      </span>
                      {showBenchmarkMarkers && bm && getGapBadge(domain.maturityLevel ?? 1, bm.target_level)}
                      {showTargetMarker && effectiveTarget && getGapBadge(domain.maturityLevel ?? 1, effectiveTarget)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          {(showTargetMarker || showBenchmarkMarkers) && (
            <div className="mt-4 pt-3 border-t flex items-center gap-4 text-[11px] text-gray-400">
              {showTargetMarker && (
                <span className="flex items-center gap-1">
                  <span className="w-3 h-0.5 bg-blue-500 rounded inline-block" /> Target level
                </span>
              )}
              {showBenchmarkMarkers && (
                <span className="flex items-center gap-1">
                  <span className="w-3 h-0.5 bg-amber-500 inline-block" /> Industry target
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
